import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    type DataTableAction,
    type DataTableColumn,
    createCurrencyFormatter,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import type { ConfigOptionPriceModalProps } from '@/modals/ServiceModals/ConfigOptionPriceModal'
import {
    deleteConfigOptionPrice,
    getConfigOptionPriceHistory,
    getServiceConfigoptionPrices,
} from '@/modules/Service/services'
import type {
    ConfigOptionPriceHistory,
    PriceRange,
    ServiceConfigoption,
} from '@/modules/Service/types'
import { formatDateWithFormatter, formatNumeral } from '@/utils/function'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { ChevronDown, ChevronRight, Pencil } from 'lucide-react'
import ConfigPriceRanges from '../../Templates/Conversant/shared/ConfigPriceRanges'
import { PRODUCT_UNITS } from '@/modules/Contract/constants'

/** Check if config option uses tiered/progressive pricing */
const hasPriceRanges = (co: ServiceConfigoption) =>
    co.price_exceed_id > 0 && co.price_ranges?.length > 0

const isUnlimited = (value: number) => Number(value) >= 999999999

// --- Flat pricing: price history table ---

const getHistoryColumns = (
    t: (key: string) => string
): DataTableColumn<ConfigOptionPriceHistory>[] => [
    {
        key: 'commitment_value',
        title: t('configoption_prices.commitment_value'),
        width: 120,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'commitment_price_per_unit',
        title: t('configoption_prices.price_per_unit'),
        width: 140,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'effective_from',
        title: t('configoption_prices.effective_from'),
        width: 140,
        render: (value) => (value ? formatDateWithFormatter(value) : '—'),
    },
    {
        key: 'effective_to',
        title: t('configoption_prices.effective_to'),
        width: 140,
        render: (value) =>
            value
                ? formatDateWithFormatter(value)
                : t('configoption_prices.ongoing'),
    },
    {
        key: 'source',
        title: t('configoption_prices.source'),
        width: 100,
        render: (value) => (
            <Typogaphy className="capitalize">{value || '—'}</Typogaphy>
        ),
    },
    {
        key: 'description',
        title: t('configoption_prices.description'),
        width: 200,
        render: (value) => value || '—',
    },
]

const FlatPriceHistory = ({
    serviceID,
    configOption,
}: {
    serviceID: number
    configOption: ServiceConfigoption
}) => {
    const { t } = useTranslation('services')
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const configID = configOption.service_configoption_id

    const {
        data: history,
        isLoading,
        isFetching,
    } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_CONFIGOPTION_PRICE_HISTORY,
            serviceID,
            configID,
        ],
        queryFn: () => getConfigOptionPriceHistory({ serviceID, configID }),
    })

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_CONFIGOPTION_PRICE_HISTORY],
    })

    const { mutateAsync: deleteMutation } = useMutationWithHandler(
        { mutationFn: deleteConfigOptionPrice },
        {
            successMessage: t('configoption_prices.delete_success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_CONFIGOPTION_PRICE_HISTORY,
            onSuccess: () => refetch(),
        }
    )

    const columns = useMemo(() => getHistoryColumns(t), [t])

    const actions: DataTableAction<ConfigOptionPriceHistory>[] = useMemo(
        () => [
            {
                key: 'edit',
                label: t('configoption_prices.edit'),
                variant: 'info',
                onClick: (row) =>
                    setModal<ConfigOptionPriceModalProps>(
                        'ServiceModals/ConfigOptionPriceModal/index',
                        {
                            serviceID,
                            configID,
                            configName: configOption.name,
                            price: row,
                        }
                    ),
            },
            {
                key: 'delete',
                label: t('common.delete'),
                variant: 'danger',
                onClick: (row) =>
                    setConfirmation({
                        open: true,
                        title: t('common.delete'),
                        description: t('configoption_prices.delete_confirm'),
                        onConfirm: () =>
                            deleteMutation({
                                serviceID,
                                configID,
                                priceID: row.id,
                            }),
                    }),
            },
        ],
        [
            t,
            serviceID,
            configID,
            configOption.name,
            setModal,
            setConfirmation,
            deleteMutation,
        ]
    )

    const addButton = (
        <Button
            key="add-config-price"
            onClick={() =>
                setModal<ConfigOptionPriceModalProps>(
                    'ServiceModals/ConfigOptionPriceModal/index',
                    { serviceID, configID, configName: configOption.name }
                )
            }
            variant="outlined"
            size="small"
        >
            <AddIcons className="w-4 h-4 mr-1" />
            {t('configoption_prices.add')}
        </Button>
    )

    return (
        <DataTableCustomized<ConfigOptionPriceHistory>
            columns={columns}
            rows={history || []}
            loading={isLoading}
            isFetching={isFetching && !isLoading}
            actions={actions}
            rowKey={(row) => row.id}
            toolbar={{ slotComponents: [addButton] }}
            emptyMessage={t('configoption_prices.no_data')}
        />
    )
}

// --- Tiered/Progressive pricing: price ranges display ---

const getRangeColumns = (
    t: (key: string) => string
): DataTableColumn<PriceRange>[] => [
    {
        key: 'price_start',
        title: t('services.usage-from'),
        width: 140,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'price_end',
        title: t('services.usage-to'),
        width: 140,
        cellClassName: 'text-right',
        render: (value) =>
            isUnlimited(value) ? (
                <Typogaphy className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                    ∞ Unlimited
                </Typogaphy>
            ) : (
                formatNumeral(value, 0)
            ),
    },
    {
        key: 'price_per_unit',
        title: t('services.price-per-unit'),
        width: 140,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
]

const TieredPriceRanges = ({
    configOption,
}: {
    configOption: ServiceConfigoption
}) => {
    const { t } = useTranslation()
    const method = configOption.method?.calc_amount_method

    const sortedRanges = useMemo(
        () =>
            [...(configOption.price_ranges || [])].sort(
                (a, b) => a.price_start - b.price_start
            ),
        [configOption.price_ranges]
    )

    const columns = useMemo(() => getRangeColumns(t), [t])

    return (
        <div>
            <div className="mb-2 text-sm text-muted-foreground">
                {t('form-of-comparison')}:{' '}
                <strong className="text-foreground">
                    {method === 'progressive'
                        ? t('services.calc-method.progressive')
                        : t('services.calc-method.agreement')}
                </strong>
            </div>
            <DataTableCustomized<PriceRange>
                columns={columns}
                rows={sortedRanges}
                rowKey={(row, i) => row.price_range_id ?? i}
                emptyMessage="—"
            />
        </div>
    )
}

// --- Config option collapsible section ---

const ConfigOptionSection = ({
    serviceID,
    configOption,
}: {
    serviceID: number
    configOption: ServiceConfigoption
}) => {
    const [expanded, setExpanded] = useState(false)
    const { t } = useTranslation('services')
    const isTiered = hasPriceRanges(configOption)
    const fmt = (v: number) => v?.toLocaleString() ?? '—'

    return (
        <div className="border rounded-sm">
            <button
                type="button"
                className="w-full flex items-center justify-between p-2 hover:bg-muted/50 transition-colors text-left"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2">
                    {expanded ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    <Typogaphy className="font-medium capitalize">
                        {t(configOption.name)}
                    </Typogaphy>
                    {configOption.unit && configOption.unit !== 'qty' && (
                        <Typogaphy className="text-muted-foreground text-sm uppercase">
                            (
                            {
                                PRODUCT_UNITS(t)?.find(
                                    (unit: { value: string }) =>
                                        unit.value === configOption.unit
                                )?.label
                            }
                            )
                        </Typogaphy>
                    )}
                    {isTiered && (
                        <Typogaphy className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                            {configOption.method?.calc_amount_method ===
                            'progressive'
                                ? 'Progressive'
                                : 'Tiered'}
                        </Typogaphy>
                    )}
                </div>
                {!isTiered && (
                    <div className="flex items-center gap-4 text-sm">
                        <Typogaphy>
                            {t('configoption_prices.commitment_value')}:{' '}
                            <strong>
                                {fmt(configOption.commitment_value)}
                            </strong>
                        </Typogaphy>
                        <Typogaphy>
                            {t('configoption_prices.price_per_unit')}:{' '}
                            <strong>
                                {fmt(configOption.commitment_price_per_unit)}
                            </strong>
                        </Typogaphy>
                        <Typogaphy>
                            {t('configoption_prices.total')}:{' '}
                            <strong>
                                {fmt(configOption.commitment_price_total)}
                            </strong>
                        </Typogaphy>
                    </div>
                )}
            </button>
            {expanded && (
                <div className="border-t p-3">
                    {isTiered ? (
                        <TieredPriceRanges configOption={configOption} />
                    ) : (
                        <FlatPriceHistory
                            serviceID={serviceID}
                            configOption={configOption}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

// --- Main component ---

const BundleConfigPrices = ({ serviceID }: { serviceID: number }) => {
    const { t } = useTranslation('services')
    const [editingRanges, setEditingRanges] = useState(false)

    // Use configoptions/prices endpoint to get price_ranges data
    const { data: configOptions, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CONFIGOPTIONS_PRICES, serviceID],
        queryFn: () => getServiceConfigoptionPrices({ serviceID }),
    })

    const hasTieredOptions = configOptions?.some(hasPriceRanges)

    if (isLoading) {
        return (
            <Paper>
                <Typogaphy className="text-primary font-medium mb-2 capitalize">
                    {t('configoption_prices.title')}
                </Typogaphy>
                <div className="text-muted-foreground text-sm py-4 text-center">
                    Loading...
                </div>
            </Paper>
        )
    }

    // When editing tiered price ranges, show the full ConfigPriceRanges editor
    if (editingRanges) {
        return (
            <Paper>
                <div className="flex items-center justify-between">
                    <Typogaphy className="text-primary font-medium capitalize">
                        {t('configoption_prices.title')}
                    </Typogaphy>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setEditingRanges(false)}
                    >
                        {t('common.cancel')}
                    </Button>
                </div>
                <ConfigPriceRanges
                    serviceID={serviceID}
                    show_price_list
                    viewOnly={false}
                    compact
                />
            </Paper>
        )
    }

    return (
        <Paper>
            <div className="flex items-center justify-between mb-2">
                <Typogaphy className="text-primary font-medium capitalize">
                    {t('configoption_prices.title')}
                </Typogaphy>
                {hasTieredOptions && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setEditingRanges(true)}
                    >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        {t('configoption_prices.edit_ranges')}
                    </Button>
                )}
            </div>
            {!configOptions?.length ? (
                <div className="text-muted-foreground text-sm py-4 text-center">
                    {t('configoption_prices.no_data')}
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {configOptions?.map((co) => (
                        <ConfigOptionSection
                            key={co.service_configoption_id}
                            serviceID={serviceID}
                            configOption={co}
                        />
                    ))}
                </div>
            )}
        </Paper>
    )
}

export default BundleConfigPrices
